import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { schemas } from '@shared/index';
import type { z } from 'zod';

export type SessionUser = z.infer<typeof schemas.identity.sessionUser>;

/**
 * Session state.
 *
 * Deliberately NOT in RTK Query: the session is a single object read once at boot and
 * replaced on sign-in or sign-out, not a cached collection that benefits from tags and
 * invalidation. Keeping it in a plain slice means guards can read it synchronously,
 * which a query hook cannot offer.
 *
 * Nothing here is persisted. Tokens live in HttpOnly cookies the client cannot read,
 * and copying the permission list into web storage would make a stale copy survive a
 * sign-out on a shared machine.
 */

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  /** Set when a session ends unexpectedly, so the login screen can explain why. */
  expiredReason: string | null;
}

const initialState: AuthState = {
  // `unknown` until `/auth/me` answers. Guards must not redirect during this window,
  // or every refresh bounces the user to the login screen before the check completes.
  status: 'unknown',
  user: null,
  expiredReason: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionLoaded(state, action: PayloadAction<SessionUser>) {
      state.status = 'authenticated';
      state.user = action.payload;
      state.expiredReason = null;
    },
    sessionAbsent(state) {
      state.status = 'anonymous';
      state.user = null;
    },
    sessionExpired(state, action: PayloadAction<string | undefined>) {
      // Only a session that EXISTED can expire. The bootstrap `/auth/me` of a visitor
      // with no session also fails its refresh and lands here, and used to greet every
      // first-time visitor with "Your session has expired" on a clean login screen.
      const wasSignedIn = state.status === 'authenticated';
      state.status = 'anonymous';
      state.user = null;
      state.expiredReason = wasSignedIn
        ? (action.payload ?? 'Your session has expired. Please sign in again.')
        : null;
    },
    signedOut(state) {
      state.status = 'anonymous';
      state.user = null;
      state.expiredReason = null;
    },
  },
});

export const { sessionLoaded, sessionAbsent, sessionExpired, signedOut } = authSlice.actions;
export const authReducer = authSlice.reducer;
